/**
 * Who is who on a Vorgang's customer: the shared words of the record's card
 * and the printed documents' cover (spec 2026-10-20, D-8). One place, so the
 * screen and the paper cannot disagree about whether the person who wrote is
 * somebody else than the customer.
 *
 * Here and not in `features/customer`: the print feature may not import
 * another feature (R-W1), and both of them read the same rule.
 */
import type { ContactOut, CustomerOut } from "@/api";

/** Blank is absent: a form that saved „ " must not print an empty line. */
export function present(value: string | null | undefined): string | null {
  const trimmed = value?.trim() ?? "";
  return trimmed === "" ? null : trimmed;
}

/** One name for comparing two: no case, no edge spaces, one space between words. */
function normalised(name: string | null | undefined): string {
  return (name ?? "").trim().toLowerCase().replace(/\s+/g, " ");
}

/**
 * What the rule reads of the customer and of the person who wrote: a full
 * `CustomerOut` / `ContactOut` fits, and so does the pick the Eingang has
 * before anything is saved (the chosen customer, or what the mail says).
 * `Partial` keeps full records and literals with more fields compiling.
 */
type CustomerNames = Pick<CustomerOut, "company" | "displayName"> & Partial<CustomerOut>;
type ContactName = Pick<ContactOut, "name"> & Partial<ContactOut>;

/**
 * The contact person is shown only where it adds somebody (spec 2026-10-20,
 * D-8): for a company the person who wrote is always another one than the
 * company, and for a private customer it is another one only when the names
 * differ. The same person twice would just print the name twice.
 */
export function otherPerson(customer: CustomerNames, contact: ContactName): boolean {
  return present(customer.company) !== null || normalised(contact.name) !== normalised(customer.displayName);
}
